import React from "react";
import { Switch, Select } from "antd";
import { AlertCircle, Wallet } from "lucide-react";
import { PaymentOption } from "../../../../components/PaymentsOption";
import { getPaymentMethodCode, PaymentMethod } from "../../../../Enums";
import { ChequeInput } from "./useCashSaleCheckout";

interface PaymentSectionProps {
  cashAmount: number;
  setCashAmount: (amount: number) => void;
  cardAmount: number;
  setCardAmount: (amount: number) => void;
  cardTxNo: string;
  setCardTxNo: (txNo: string) => void;
  cardBankAccountId: string;
  setCardBankAccountId: (id: string) => void;
  transferAmount: number;
  setTransferAmount: (amount: number) => void;
  transferTxNo: string;
  setTransferTxNo: (txNo: string) => void;
  transferBankAccountId: string;
  setTransferBankAccountId: (id: string) => void;
  cheques: ChequeInput[];
  setCheques: (cheques: ChequeInput[]) => void;
  paymentMode: string;
  isCreditSale: boolean;
  bankAccounts: any[];
  paymentModeOptions: any[];
  cashAmountRef: any;
  cardAmountRef: any;
  transferAmountRef: any;
  chequeAmountRef: any;
  handleCreditSaleChange: (checked: boolean) => void;
  handlePaymentModeChange: (mode: string) => void;
}

export const PaymentSection: React.FC<PaymentSectionProps> = ({
  cashAmount,
  setCashAmount,
  cardAmount,
  setCardAmount,
  cardTxNo,
  setCardTxNo,
  cardBankAccountId,
  setCardBankAccountId,
  transferAmount,
  setTransferAmount,
  transferTxNo,
  setTransferTxNo,
  transferBankAccountId,
  setTransferBankAccountId,
  cheques,
  setCheques,
  paymentMode,
  isCreditSale,
  bankAccounts,
  paymentModeOptions,
  cashAmountRef,
  cardAmountRef,
  transferAmountRef,
  chequeAmountRef,
  handleCreditSaleChange,
  handlePaymentModeChange,
}) => {
  return (
    <>
      <div className="flex items-center justify-between p-2.5 bg-red-50/10 border border-red-150/20 rounded-xl">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold text-gray-800 flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4 text-red-500" /> Credit Sale (Buy
            Now, Pay Later)
          </span>
        </div>
        <Switch
          checked={isCreditSale}
          onChange={handleCreditSaleChange}
          className={isCreditSale ? "bg-red-500" : "bg-gray-200"}
        />
      </div>

      {!isCreditSale && (
        <>
          {/* Payment Method Selector Dropdown */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
              <Wallet className="w-3.5 h-3.5 text-gray-400" /> Payment Method
            </label>
            <Select
              value={paymentMode}
              onChange={handlePaymentModeChange}
              size="middle"
              className="w-full"
              options={paymentModeOptions}
            />
          </div>

          <div className="flex flex-col gap-3">
            {paymentMode === "split" ? (
              <>
                <PaymentOption
                  ref={cashAmountRef}
                  paymentMethodCode={getPaymentMethodCode(PaymentMethod.CASH)}
                  value={{ amount: cashAmount }}
                  onChange={(val) => setCashAmount(val.amount ?? 0)}
                  bankAccounts={bankAccounts}
                />
                <PaymentOption
                  ref={cardAmountRef}
                  paymentMethodCode={getPaymentMethodCode(PaymentMethod.CARD)}
                  value={{
                    amount: cardAmount,
                    bank_account_id: cardBankAccountId,
                    transaction_no: cardTxNo,
                  }}
                  onChange={(val) => {
                    setCardAmount(val.amount ?? 0);
                    setCardBankAccountId(val.bank_account_id ?? "");
                    setCardTxNo(val.transaction_no ?? "");
                  }}
                  bankAccounts={bankAccounts}
                />
                <PaymentOption
                  ref={transferAmountRef}
                  paymentMethodCode={getPaymentMethodCode(
                    PaymentMethod.BANK_TRANSFER,
                  )}
                  value={{
                    amount: transferAmount,
                    bank_account_id: transferBankAccountId,
                    reference_no: transferTxNo,
                  }}
                  onChange={(val) => {
                    setTransferAmount(val.amount ?? 0);
                    setTransferBankAccountId(val.bank_account_id ?? "");
                    setTransferTxNo(val.reference_no ?? "");
                  }}
                  bankAccounts={bankAccounts}
                />
                <PaymentOption
                  ref={chequeAmountRef}
                  paymentMethodCode={getPaymentMethodCode(PaymentMethod.CHEQUE)}
                  value={{ cheques: cheques }}
                  onChange={(val) => setCheques(val.cheques ?? [])}
                  bankAccounts={bankAccounts}
                />
              </>
            ) : (
              <PaymentOption
                ref={
                  paymentMode === getPaymentMethodCode(PaymentMethod.CASH)
                    ? cashAmountRef
                    : paymentMode === getPaymentMethodCode(PaymentMethod.CARD)
                      ? cardAmountRef
                      : paymentMode ===
                          getPaymentMethodCode(PaymentMethod.BANK_TRANSFER)
                        ? transferAmountRef
                        : chequeAmountRef
                }
                paymentMethodCode={paymentMode}
                value={{
                  amount:
                    paymentMode === getPaymentMethodCode(PaymentMethod.CASH)
                      ? cashAmount
                      : paymentMode === getPaymentMethodCode(PaymentMethod.CARD)
                        ? cardAmount
                        : paymentMode ===
                            getPaymentMethodCode(PaymentMethod.BANK_TRANSFER)
                          ? transferAmount
                          : 0,
                  bank_account_id:
                    paymentMode === getPaymentMethodCode(PaymentMethod.CARD)
                      ? cardBankAccountId
                      : paymentMode ===
                          getPaymentMethodCode(PaymentMethod.BANK_TRANSFER)
                        ? transferBankAccountId
                        : "",
                  transaction_no: cardTxNo,
                  reference_no: transferTxNo,
                  cheques: cheques,
                }}
                onChange={(val) => {
                  if (
                    paymentMode === getPaymentMethodCode(PaymentMethod.CASH)
                  ) {
                    setCashAmount(val.amount ?? 0);
                  } else if (
                    paymentMode === getPaymentMethodCode(PaymentMethod.CARD)
                  ) {
                    setCardAmount(val.amount ?? 0);
                    setCardBankAccountId(val.bank_account_id ?? "");
                    setCardTxNo(val.transaction_no ?? "");
                  } else if (
                    paymentMode ===
                    getPaymentMethodCode(PaymentMethod.BANK_TRANSFER)
                  ) {
                    setTransferAmount(val.amount ?? 0);
                    setTransferBankAccountId(val.bank_account_id ?? "");
                    setTransferTxNo(val.reference_no ?? "");
                  } else if (
                    paymentMode === getPaymentMethodCode(PaymentMethod.CHEQUE)
                  ) {
                    setCheques(val.cheques ?? []);
                  }
                }}
                bankAccounts={bankAccounts}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};
